import { IButtonStyles, Position } from "@fluentui/react";
import { IKeytipProps } from "@fluentui/react/lib/Keytip";
import { ISpinButtonStyles } from "@fluentui/react/lib/SpinButton";

import {
  AppControlBaseProps,
  AppControlBaseStoreOnlyProps,
  OnExecuteParameter,
  UILocation,
} from "@1js/acui-common";

export const AppSpinButtonUnsetValue = "";
export type AppSpinButtonValue = number | "";
export interface AppSpinButtonStoreOnlyProps
  extends AppControlBaseStoreOnlyProps {
  /**
   * The displayed number in the spin button
   */
  value: AppSpinButtonValue;

  /**
   * The displayed unit in the spin button
   */
  unitType: string;

  /**
   * The valid units for the Spin Button
   */
  validUnits: AppSpinButtonUnit[];

  /**
   * The current special string that is displayed in the Spin Button. For example, 'Auto'.
   */
  reservedValue?: string;

  /**
   * Special strings that can be displayed in the Spin Button. For example, 'Auto'.
   */
  validReservedValues?: string[];
}

export interface AppSpinButtonProps
  extends AppControlBaseProps,
    AppSpinButtonStoreOnlyProps {
  type: "AppSpinButtonProps";

  /**
   * The text to show alongside the spin button
   */
  label: string;

  /**
   * The information that defines the number's format. It includes information such as, negative sign, decimal separator, number of decimals.
   */
  numberFormatInfo: NumberFormatInfo;

  /**
   * Position of the label in the control
   */
  labelPosition?: Position;

  /**
   * The name of the icon to show for this button
   */
  icon?: string;

  /**
   * The modified color of the icon for this button
   */
  iconColor?: string;

  /**
   * Function that gets invoked when an update to the value of the spin button control is committed by the user.
   * The callback provides the id of the component that invoked the event and the new value with unit type.
   * It also can contain a special string in case the spin button accepts special text.
   * It may also accept a boolean that indicates whether the commanding surface should keep focus
   */
  onExecute?: (parameter: AppSpinButtonOnExecuteParameter) => void;

  /**
   * Function that can be used to normalize the incremented/decremented values given by spin button control.
   * It accepts the incremented/decremented value by spin control as input and returns the normalized value to be displayed finally on the spin control UI.
   */
  /**
   * For eq: to mimic circular spin behavior: This function can be used to normalize the spin button behaviour like minValue.minValue+1...maxValue.minValue.minValue+1...
   */
  customNormalizeValue?: (value: number, unit: AppSpinButtonUnit) => number;

  /**
   * Custom Styling
   */
  styles?: Partial<ISpinButtonStyles>;

  /**
   * Custom Styling for the arrow buttons
   */
  buttonStyles?: Partial<IButtonStyles>;

  /**
   * The full description of the control which can be used to provide
   * more context to what the control is used for.
   */
  fullDescriptionLabel?: string;

  /**
   * Aria Label the for decrement button in our spin button control.
   */
  decrementAriaLabel?: string;

  /**
   * Aria Label for the increment button in our spin button control.
   */
  incrementAriaLabel?: string;

  /**
   * Optional, the position in the parent set (if in a set)
   */
  positionInSet?: number;

  /**
   * Optional, the total size of the parent set (if in a set)
   */
  setSize?: number;

  /**
   * Optional Keytip for this SpinButton. Will be put on the input component
   */
  keytipProps?: IKeytipProps;
}

export interface AppSpinButtonUnit {
  /** The type of unit measurement */
  unitType: string;

  /** The string representation of this unit with a placeholder for the value*/
  unitPlaceholder: string;

  /** The minimum acceptable value */
  minValue: number;

  /** The maximum acceptable value */
  maxValue: number;

  /** the amount by which the value should be incremented/decremented on arrow button press */
  step: number;
}

/** The information that defines the number's format. */
export interface NumberFormatInfo {
  /** The string that represents negative sign */
  negativeSign: string;

  /** The number of decimal digits to be displayed in the Spin Button */
  numberDecimalDigits: number;

  /** The string that separates the number into whole and decimal parts */
  numberDecimalSeparator: string;

  /** The pattern of the structure of a negative number */
  numberNegativePattern: number;

  /** The string that represents a separator between digits. For example, using comma for thousands '1,000' */
  numberGroupSeparator: string;

  /** An array containing the number of digits for each group in the integer part of a number.
   * For example, the array [3, 2, 1] with comma separator will convert number 567890 into 5,76,890
   * That is, put a comma after the first three digits (left to right),
   * then put another comma after two more digits, and so on.*/
  numberGroupSizes: number[];
}

export type withoutSpinButtonStoreProps<T extends AppSpinButtonProps> = Omit<
  T,
  keyof AppSpinButtonStoreOnlyProps
>;

export type AppSpinButtonInheritedProps =
  withoutSpinButtonStoreProps<AppSpinButtonProps>;

export type AppSpinButtonUnionProps =
  | AppSpinButtonProps
  | AppSpinButtonInheritedProps;

/**
 * These are props that are specific to the Ribbon framework's implementation of a Spin Button.
 * It includes implementation details that we don't expect apps to pass for each instance of the control.
 * An example would be icons that are used by every instance of the control, like a chevron.
 */
export interface AppSpinButtonInternalProps extends AppSpinButtonProps {
  /** The name of the icon to show for the increment button  */
  incrementButtonIcon: string;

  /** The name of the icon to show for decrement button */
  decrementButtonIcon: string;
}

export interface AppSpinButtonOnExecuteParameter extends OnExecuteParameter {
  value: AppSpinButtonValue;
  unitType: string;
  reservedValue?: string;
  location?: UILocation;
  shouldTakeFocus?: boolean;
}
